import { useCallback, useMemo, useRef, useState } from "react";
import useTagAutocomplete from "domains/assets/hooks/useTagAutocomplete";
import HorizontalScrollable, {
  HorizontalScrollableProps,
} from "domains/commons/components/HorizontalScrollable";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { expandTagItems } from "domains/tags/utils";
import { isTagValid } from "domains/tags/utils/validateTag";
import Button from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import ScenarioInput from "domains/ui/components/ScenarioInput";

import { Box, HStack, Text, useDisclosure, VStack } from "@chakra-ui/react";

interface TagInputProps {
  currentTags: string[];
  onAddTags: (tags: string[]) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  gradientColor?: HorizontalScrollableProps["gradientColor"];
}

export default function TagInput({
  currentTags,
  onAddTags,
  inputRef,
  gradientColor,
}: TagInputProps) {
  const { errorToast } = useScenarioToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newTag, setNewTag] = useState("");
  const tagsElementRef = useRef<HTMLDivElement>(null);

  const { tags, isLoading: isLoadingAutocomplete } = useTagAutocomplete(newTag);
  const options = useMemo(
    () => tags?.filter((tag) => !currentTags.includes(tag)),
    [tags, currentTags]
  );

  // ----------------------------------

  const addTag = useCallback(
    (tag: string) => {
      const newPotentialTag = isTagValid(tag);

      if (newPotentialTag.isValid) {
        if (currentTags.includes(tag)) {
          errorToast({
            title: "Tag already exists",
          });
          return;
        }
        onAddTags(expandTagItems(tag));
        setNewTag("");
      } else {
        errorToast({
          title: newPotentialTag.message,
        });
      }
    },
    [currentTags, errorToast, onAddTags]
  );

  // ----------------------------------

  const handleAddTag = useCallback(() => addTag(newTag), [addTag, newTag]);

  const handleAutocompleteMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
    },
    []
  );

  const handleAutocompleteClick = useCallback(
    (value: string) => {
      setNewTag(value);
      setTimeout(() => addTag(value), 1);
    },
    [setNewTag, addTag]
  );

  // ----------------------------------

  return (
    <VStack align="stretch" w="100%" spacing={2}>
      <ScenarioInput
        ref={inputRef}
        value={newTag}
        setValue={setNewTag}
        placeholder="Add a tag"
        onEnter={handleAddTag}
        onFocus={onOpen}
        onBlur={onClose}
        containerProps={{ h: 8 }}
        leftComponent={
          <Text mt={"2px"} size="body.lg">
            #
          </Text>
        }
      />

      {isOpen && options?.length ? (
        <HorizontalScrollable
          childrenRef={tagsElementRef}
          gradientColor={gradientColor ?? "#121212"}
          onMouseDown={handleAutocompleteMouseDown}
          size="sm"
        >
          <HStack
            ref={tagsElementRef}
            sx={{
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
            overflow="auto"
            spacing={1}
          >
            {options.map((option, idx) => (
              <Button
                key={`${option}-${idx}`}
                size="xs"
                width="auto"
                variant="menuItem"
                pointerEvents={isLoadingAutocomplete ? "none" : "auto"}
                leftIcon={<Icon id="Ui/Clock" h="12px" />}
                onClick={() => handleAutocompleteClick(option)}
                fontSize="12px"
              >
                <HStack spacing={0}>
                  <Box as="span" color="textSecondary" whiteSpace="pre">
                    {newTag}
                  </Box>
                  <Box as="span" color="textPrimary" whiteSpace="pre">
                    {option.replace(newTag, "")}
                  </Box>
                </HStack>
              </Button>
            ))}
          </HStack>
        </HorizontalScrollable>
      ) : (
        <HStack h="28px" px={2} color="textTertiary">
          <Icon id="Ui/Clock" h="12px" />
          <Text color="inherit" size="body.sm">
            {options?.length === 0
              ? "Press Enter to submit a new tag"
              : "Type to find recent tags"}
          </Text>
        </HStack>
      )}
    </VStack>
  );
}
